// src/admin/pages/employee/EmployeeManagement.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import BadgeIcon from '@mui/icons-material/Badge';
import { useAuth } from '../../../contexts/AuthContext';
import { EmployeeService } from './employeeService';
import { Employee, EmployeeFormData, EMPLOYEE_STATUSES } from '../../../enums/employee';
import { WarehouseService } from '../branch/branchService';
import { Warehouse } from '../../../enums/product';
import EmployeeTable from './EmployeeTable';
import EmployeeDrawer from './EmployeeDrawer';
import ToastNotification from '../common/Toastnotification';

const EMPLOYEES_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const EmployeeManagement: React.FC = () => {
  const { currentUser, userProfile, hasPermission, isSuperAdmin } = useAuth();
  const userName = userProfile?.displayName || currentUser?.email || 'Admin';

  const canCreate = isSuperAdmin || hasPermission('employees.create');
  const canEdit = isSuperAdmin || hasPermission('employees.edit');
  const canDelete = isSuperAdmin || hasPermission('employees.delete');

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // ── Data state ────────────────────────────────────────────────────────────
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [branches, setBranches] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [editing, setEditing] = useState<Employee | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);
  const [deleteLoading, setDeleteLoading] = useState('');
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  // ── Search & filter state ────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const showToast = (message: string, severity: ToastState['severity']) =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const [emps, branchList] = await Promise.all([
        EmployeeService.getAll(),
        WarehouseService.getActiveWarehouses(),
      ]);
      setEmployees(emps);
      setBranches(branchList);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(
    () => ({
      total: employees.length,
      active: employees.filter((e) => e.status === 'Active').length,
      onLeave: employees.filter((e) => e.status === 'On Leave').length,
      inactive: employees.filter((e) => e.status === 'Suspended' || e.status === 'Terminated').length,
    }),
    [employees]
  );

  // ── Filtering (client-side) ──────────────────────────────────────────────
  const filtered = useMemo(() => {
    return employees.filter((e) => {
      const matchesStatus = statusFilter === 'all' || e.status === statusFilter;
      const q = searchQuery.trim().toLowerCase();
      const matchesSearch =
        !q ||
        e.fullName?.toLowerCase().includes(q) ||
        e.phone?.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [employees, statusFilter, searchQuery]);

  const totalFiltered = filtered.length;
  const totalPages = Math.ceil(totalFiltered / EMPLOYEES_PER_PAGE);
  const pageItems = filtered.slice((currentPage - 1) * EMPLOYEES_PER_PAGE, currentPage * EMPLOYEES_PER_PAGE);

  const resetToFirstPage = () => setCurrentPage(1);
  const handlePageChange = (_e: React.ChangeEvent<unknown>, page: number) => setCurrentPage(page);

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    resetToFirstPage();
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(Boolean).length;

  // ── Add / Edit drawer ─────────────────────────────────────────────────────
  const openCreate = () => {
    setEditing(null);
    setDrawerMode('add');
    setIsDrawerOpen(true);
  };

  const openEdit = (emp: Employee) => {
    setEditing(emp);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setEditing(null);
  };

  const handleSave = async (formData: EmployeeFormData) => {
    try {
      if (drawerMode === 'edit' && editing?.id) {
        await EmployeeService.update(editing.id, formData, currentUser?.uid || '', userName);
        showToast('Employee updated successfully', 'success');
      } else {
        await EmployeeService.create(formData, currentUser?.uid || '', userName);
        showToast('Employee added successfully', 'success');
      }
      handleDrawerClose();
      await load();
    } catch (err: any) {
      showToast('Failed to save employee: ' + (err.message || 'Unknown error'), 'error');
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id);
      await EmployeeService.delete(deleteTarget.id, currentUser?.uid || '', userName);
      setDeleteTarget(null);
      showToast('Employee removed successfully', 'success');
      resetToFirstPage();
      await load();
    } catch (err: any) {
      showToast('Failed to remove employee: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleRefresh = () => {
    load();
    showToast('Employees refreshed', 'info');
  };

  // ── Initial full-page loading state ─────────────────────────────────────
  if (loading && employees.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px" flexDirection="column">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading employees...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <BadgeIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Employees
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Staff records, roles, and salary details
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button onClick={handleRefresh} variant="outlined" startIcon={<RefreshIcon />} disabled={loading}>
              Refresh
            </Button>
            {canCreate && (
              <Button onClick={openCreate} variant="contained" startIcon={<AddIcon />}>
                Add Employee
              </Button>
            )}
          </Stack>
        </Stack>

        {/* ── Error banner ── */}
        {errorMsg && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                Retry
              </Button>
            }
          >
            {errorMsg}
          </Alert>
        )}

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
          {[
            { label: 'Total Employees', value: stats.total },
            { label: 'Active', value: stats.active },
            { label: 'On Leave', value: stats.onLeave },
            { label: 'Suspended / Terminated', value: stats.inactive },
          ].map((s) => (
            <Grid item xs={6} sm={6} md={3} key={s.label} sx={{ display: 'flex' }}>
              <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                    flex: 1,
                  }}
                >
                  <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                    {s.label}
                  </Typography>
                  <Typography
                    variant="h5"
                    component="div"
                    color="text.primary"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}
                  >
                    {s.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search by name, phone, or email... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      {EMPLOYEE_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${totalFiltered} employees matching your filters`
                        : `Showing ${totalFiltered} employees`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {/* ── Empty state ── */}
        {pageItems.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <BadgeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Employees Match Your Filters' : 'No Employees Yet'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No employees added yet. Add your first employee to get started.'}
                </Typography>
                {hasActiveFilters ? (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
                    Clear Filters
                  </Button>
                ) : (
                  canCreate && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                      Add Employee
                    </Button>
                  )
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* ── Table + Pagination ── */}
        {pageItems.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <Box
              ref={tableWrapperRef}
              sx={{ flex: 1, overflow: 'auto', mb: 2, minHeight: { xs: 'auto', md: 0 } }}
            >
              <EmployeeTable
                employees={pageItems}
                branches={branches}
                onEdit={openEdit}
                onDelete={setDeleteTarget}
                deleteLoading={deleteLoading}
                isDark={isDark}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            </Box>

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalFiltered === 0 ? 0 : (currentPage - 1) * EMPLOYEES_PER_PAGE + 1}–
                    {Math.min(currentPage * EMPLOYEES_PER_PAGE, totalFiltered)} of {totalFiltered} employees
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Add / Edit Drawer ── */}
      <EmployeeDrawer
        isOpen={isDrawerOpen}
        mode={drawerMode}
        employee={editing}
        branches={branches}
        onClose={handleDrawerClose}
        onSave={handleSave}
      />

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Employee</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove <strong>"{deleteTarget?.fullName}"</strong>? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteDialogLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteDialogLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Toast ── */}
      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={handleCloseToast} />
    </Box>
  );
};

export default EmployeeManagement;
